import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { phoneStyles } from '../../styles/phone-styles.js';
import { twilioService } from '../../services/twilio-service.js';
import '../voice-modal.js';

/**
 * Phone Test Call Dialog — sub-component
 * Self-contained dialog for making test calls from a phone number.
 * Opens via `number` property, dispatches `close` event when done.
 */
export class PhoneTestModal extends LitElement {
  static properties = {
    number: { type: Object },       // { raw, phone } — the FROM number
    _to: { type: String, state: true },
    _loading: { type: Boolean, state: true },
    _result: { type: Object, state: true }
  };

  static styles = [designSystem, componentStyles, phoneStyles, css`:host { display: block; }`];

  constructor() {
    super();
    this.number = null;
    this._to = '';
    this._loading = false;
    this._result = null;
  }

  _close() {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }

  async _execute() {
    if (this._loading) return;

    // Normalize input
    let to = this._to.replace(/[\s\-\(\)\.]/g, '');

    // PH number conversion
    if (/^09\d{9}$/.test(to)) {
      to = '+63' + to.substring(1);
    }

    // Prepend + if just digits
    if (/^[1-9]\d{6,14}$/.test(to)) {
      to = '+' + to;
    }

    // Validate E.164
    if (!/^\+[1-9]\d{1,14}$/.test(to)) {
      this._result = { validationError: 'Invalid number format. Use E.164 (e.g. +15551234567 or +639556682474)' };
      return;
    }

    this._to = to;
    this._loading = true;
    this._result = null;

    try {
      const data = await twilioService.makeTestCall(this.number.raw, to);
      if (data.success) {
        this._result = { success: true, sid: data.call?.sid || '', status: data.call?.status || 'queued' };
      } else {
        this._result = { error: data.error || 'Failed to initiate call' };
      }
    } catch (err) {
      this._result = { error: 'Network error — could not reach the server' };
    } finally {
      this._loading = false;
    }
  }

  render() {
    if (!this.number) return '';

    return html`
      <voice-modal ?open=${!!this.number} title="Test Call" width="440px"
        @modal-close=${this._close}>
        <div style="padding:var(--space-6, 24px);">
        <div class="test-from">
          <span class="test-from-label">From</span>
          <span class="test-from-number">${this.number.phone || this.number.raw}</span>
        </div>

        <div class="test-input-group">
          <label>Destination Number</label>
          <input
            type="tel"
            class="test-input ${this._result?.validationError ? 'error' : ''}"
            placeholder="+1 (555) 123-4567"
            .value=${this._to}
            @input=${(e) => { this._to = e.target.value; this._result = null; }}
            @keydown=${(e) => { if (e.key === 'Enter' && !this._loading) this._execute(); }}
            ?disabled=${this._loading || this._result?.success}
          />
          ${this._result?.validationError ? html`
            <div class="test-error-hint">${this._result.validationError}</div>
          ` : html`
            <div class="test-hint">Enter the phone number to call in E.164 format (e.g. +15551234567)</div>
          `}
        </div>

        ${this._result?.success ? html`
          <div class="test-result success">
            <i class="fa-sharp fa-light fa-circle-check"></i>
            Call initiated successfully! Status: <strong>${this._result.status}</strong>
            <div class="test-result-sid">Call SID: ${this._result.sid}</div>
          </div>
        ` : ''}

        ${this._result?.error ? html`
          <div class="test-result error">
            <i class="fa-sharp fa-light fa-circle-exclamation"></i>
            ${this._result.error}
          </div>
        ` : ''}
        </div>

        <div slot="footer">
          <button class="btn btn-secondary" @click=${this._close}>
            ${this._result?.success ? 'Close' : 'Cancel'}
          </button>
          ${!this._result?.success ? html`
            <button class="btn btn-primary" @click=${this._execute} ?disabled=${this._loading}>
              ${this._loading ? html`
                <i class="fa-sharp fa-light fa-spinner-third fa-spin"></i> Calling...
              ` : html`
                <i class="fa-sharp fa-light fa-phone"></i> Make Call
              `}
            </button>
          ` : ''}
        </div>
      </voice-modal>
    `;
  }
}

customElements.define('phone-test-modal', PhoneTestModal);
